// src/pages/Trend/components/analysis/market-trends/PriceTrendChart.js
import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import './PriceTrendChart.css';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList
} from 'recharts';
import { ErrorMessage } from '../../../../../components/common/UI';
import { BBC_STYLE } from '../../../../../styles/chartStyles';
import ChartContainer from '../common/ChartContainer';

// Custom Annotation Component - BBC style with translated text
const AnnotationLabel = ({ x, y, width, height, value, isMaxValue, totalProducts, isPrimary, index, dataIndex, translate }) => {
  if (!isMaxValue || index !== dataIndex) return null;

  const xPos = x + width / 2;
  // Both annotations on top with offset for secondary
  const yPos = isPrimary ? y - 25 : y - 50;
  const percentage = ((value / totalProducts) * 100).toFixed(0);
  const color = isPrimary ? BBC_STYLE.PRIMARY_COLOR : BBC_STYLE.SECONDARY_COLOR;

  return (
    <g>
      {/* Background rectangle for better readability */}
      <rect
        x={xPos - 60}
        y={yPos - 14}
        width={120}
        height={22}
        fill="white"
        fillOpacity="0.95"
        rx="2"
        ry="2"
      />
      {/* Text with percentage */}
      <text
        x={xPos}
        y={yPos}
        fill={color}
        textAnchor="middle"
        fontSize="12px"
        fontWeight="600"
        fontFamily={BBC_STYLE.FONT_FAMILY}
      >
        {translate('trendPage.marketTrends.narratives.price.single.concentration', { percentage })}
      </text>
      {/* Downward pointing triangle */}
      <path
        d={`M ${xPos} ${yPos + 10} L ${xPos - 5} ${yPos + 5} L ${xPos + 5} ${yPos + 5} Z`}
        fill={color}
      />
    </g>
  );
};

// Process chart data
const processChartData = (primary, secondary, translate) => {
  const hasSecondaryData = secondary?.price_analysis?.price_distribution;

  return primary.price_analysis.price_distribution.labels.map((label, index) => ({
    range: label,
    [translate('trendPage.filters.set1')]: primary.price_analysis.price_distribution.values[index],
    ...(hasSecondaryData ? {
      [translate('trendPage.filters.set2')]: secondary.price_analysis.price_distribution.values[index]
    } : {})
  }));
};

// Get total products
const getTotalProducts = data => {
  return data.price_distribution.values.reduce((sum, value) => sum + value, 0);
};

// Create narrative title based on data insights
const createNarrativeTitle = (primary, secondary, hasSecondaryData, translate) => {
  const primaryDistribution = primary.price_analysis.price_distribution;
  const maxPrimaryValue = Math.max(...primaryDistribution.values);
  const maxPrimaryIndex = primaryDistribution.values.indexOf(maxPrimaryValue);
  const maxPriceRange = primaryDistribution.labels[maxPrimaryIndex];
  const totalProducts = getTotalProducts(primary.price_analysis);
  const percentage = ((maxPrimaryValue / totalProducts) * 100).toFixed(0);

  if (!hasSecondaryData) {
    // Single narrative without comparison
    const narrativeText = translate('trendPage.marketTrends.narratives.price.single.concentrationIn', {
      range: maxPriceRange,
      percentage: percentage
    });

    // Split the narrative to highlight the range
    const parts = narrativeText.split(maxPriceRange);

    return (
      <>
        {parts[0]}
        <span style={{
          fontWeight: '700',
          color: BBC_STYLE.HIGHLIGHT_COLOR,
          borderBottom: `2px solid ${BBC_STYLE.HIGHLIGHT_COLOR}`,
          paddingBottom: '1px'
        }}>
          {maxPriceRange}
        </span>
        {parts[1]}
      </>
    );
  }

  // Comparison mode - simpler narratives
  const secondaryDistribution = secondary.price_analysis.price_distribution;
  const maxSecondaryValue = Math.max(...secondaryDistribution.values);
  const maxSecondaryIndex = secondaryDistribution.values.indexOf(maxSecondaryValue);
  const maxSecondaryRange = secondaryDistribution.labels[maxSecondaryIndex];
  const secondaryTotal = getTotalProducts(secondary.price_analysis);
  const secondaryPercentage = ((maxSecondaryValue / secondaryTotal) * 100).toFixed(0);

  // Combined insight with price difference
  const samePriceRange = maxPrimaryIndex === maxSecondaryIndex;

  const combinedInsight = samePriceRange
    ? translate('trendPage.marketTrends.narratives.price.comparison.bothFocused', { range: maxPriceRange })
    : translate('trendPage.marketTrends.narratives.price.comparison.differentPreference');

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '10px'
    }}>
      {/* Combined insight first */}
      <div style={{
        fontSize: '1.3rem',
        fontWeight: '600',
        color: BBC_STYLE.TEXT_COLOR
      }}>
        {combinedInsight}
      </div>

      {/* Individual narratives - price range only */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '12px',
        fontSize: '0.95rem'
      }}>
        {/* Primary narrative */}
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px'
        }}>
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: BBC_STYLE.PRIMARY_COLOR
          }} />
          <span>
            {translate('trendPage.filters.set1')}: {' '}
            <span style={{
              fontWeight: '700',
              color: BBC_STYLE.PRIMARY_COLOR
            }}>
              {maxPriceRange}
            </span>
            {' '}({percentage}%)
          </span>
        </div>

        {/* Secondary narrative */}
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px'
        }}>
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: BBC_STYLE.SECONDARY_COLOR
          }} />
          <span>
            {translate('trendPage.filters.set2')}: {' '}
            <span style={{
              fontWeight: '700',
              color: BBC_STYLE.SECONDARY_COLOR
            }}>
              {maxSecondaryRange}
            </span>
            {' '}({secondaryPercentage}%)
          </span>
        </div>
      </div>
    </div>
  );
};

// Create subtitle with key insights using translations
const createSubtitle = (primary, secondary, hasSecondaryData, translate) => {
  const avgPrice = primary.price_analysis.avg_price;
  const avgDiscount = primary.price_analysis.avg_discount;
  const totalProducts = getTotalProducts(primary.price_analysis);

  // Find the concentration level
  const primaryDistribution = primary.price_analysis.price_distribution;
  const maxValue = Math.max(...primaryDistribution.values);
  const concentration = ((maxValue / totalProducts) * 100).toFixed(0);

  // Check discount level
  const highDiscount = avgDiscount > 10;

  // Helper function for formatting price based on language
  const formatPriceForDisplay = (price) => {
    const currentLang = localStorage.getItem('language') || 'ko';
    if (currentLang === 'en') {
      return '₩' + price.toLocaleString('en-US');
    } else {
      return price.toLocaleString('ko-KR') + '원';
    }
  };

  if (hasSecondaryData) {
    const secondaryAvgPrice = secondary.price_analysis.avg_price;
    const secondaryTotal = getTotalProducts(secondary.price_analysis);

    const priceDiff = Math.abs(avgPrice - secondaryAvgPrice);

    // Get currency suffix/prefix based on locale
    const formatPrice = (price) => {
      const currentLang = localStorage.getItem('language') || 'ko';
      // Format with thousand separators
      if (currentLang === 'en') {
        return '₩' + price.toLocaleString('en-US');
      } else {
        return price.toLocaleString('ko-KR') + '원';
      }
    };

    return (
      <>
        {(() => {
          const text = translate('trendPage.marketTrends.narratives.price.comparison.set1Summary', {
            count: totalProducts,
            price: formatPrice(avgPrice)
          });

          const currentLang = localStorage.getItem('language') || 'ko';

          // Match product count for both languages
          const countMatch = currentLang === 'ko'
            ? text.match(/(\d+)개 제품/)
            : text.match(/(\d+)\s+products?/i);

          // Match price with comma-separated thousands for both languages
          const priceMatch = currentLang === 'ko'
            ? text.match(/([\d,]+원)/)
            : text.match(/(₩[\d,]+)/);

          let result = text;

          if (countMatch) {
            const numberToHighlight = currentLang === 'ko' ? countMatch[1] : countMatch[1];
            result = result.replace(numberToHighlight,
              `<span style="font-weight: 700; font-size: 1.15rem; color: ${BBC_STYLE.PRIMARY_COLOR}">${numberToHighlight}</span>`
            );
          }

          if (priceMatch) {
            result = result.replace(priceMatch[0],
              `<span style="font-weight: 600; background-color: rgba(19, 128, 161, 0.08); padding: 2px 6px; border-radius: 3px">${priceMatch[0]}</span>`
            );
          }

          return <span dangerouslySetInnerHTML={{ __html: result }} />;
        })()}
        <span style={{
          margin: '0 10px',
          color: '#D0D0D0',
          fontWeight: '300'
        }}>｜</span>
        {(() => {
          const text = translate('trendPage.marketTrends.narratives.price.comparison.set2Summary', {
            count: secondaryTotal,
            price: formatPrice(secondaryAvgPrice)
          });

          const currentLang = localStorage.getItem('language') || 'ko';

          // Match product count for both languages
          const countMatch = currentLang === 'ko'
            ? text.match(/(\d+)개 제품/)
            : text.match(/(\d+)\s+products?/i);

          // Match price with comma-separated thousands for both languages
          const priceMatch = currentLang === 'ko'
            ? text.match(/([\d,]+원)/)
            : text.match(/(₩[\d,]+)/);

          let result = text;

          if (countMatch) {
            const numberToHighlight = currentLang === 'ko' ? countMatch[1] : countMatch[1];
            result = result.replace(numberToHighlight,
              `<span style="font-weight: 700; font-size: 1.15rem; color: ${BBC_STYLE.SECONDARY_COLOR}">${numberToHighlight}</span>`
            );
          }

          if (priceMatch) {
            result = result.replace(priceMatch[0],
              `<span style="font-weight: 600; background-color: rgba(250, 171, 24, 0.08); padding: 2px 6px; border-radius: 3px">${priceMatch[0]}</span>`
            );
          }

          return <span dangerouslySetInnerHTML={{ __html: result }} />;
        })()}
        {priceDiff > 1000 && (
          <>
            <span style={{
              margin: '0 10px',
              color: '#D0D0D0',
              fontWeight: '300'
            }}>｜</span>
            <span style={{
              fontWeight: '600',
              fontSize: '0.9rem',
              color: BBC_STYLE.HIGHLIGHT_COLOR,
              backgroundColor: 'rgba(213, 94, 0, 0.08)',
              padding: '2px 6px',
              borderRadius: '3px'
            }}>
              {translate('trendPage.marketTrends.narratives.price.comparison.priceDiff', {
                diff: formatPrice(priceDiff)
              })}
            </span>
          </>
        )}
      </>
    );
  }

  return (
    <>
      {translate('trendPage.marketTrends.narratives.price.single.totalProducts', {
        count: totalProducts
      }).split(totalProducts.toString()).map((part, index) => {
        if (index === 0) {
          return (
            <span key={index}>
              {part}
              <span style={{
                fontWeight: '700',
                fontSize: '1.15rem',
                color: BBC_STYLE.PRIMARY_COLOR
              }}>
                {totalProducts}
              </span>
            </span>
          );
        }
        return <span key={index}>{part}</span>;
      })}
      <span style={{
        margin: '0 10px',
        color: '#D0D0D0',
        fontWeight: '300'
      }}>｜</span>
      {translate('trendPage.marketTrends.narratives.price.single.avgPrice', {
        price: avgPrice.toLocaleString('ko-KR')
      }).split(avgPrice.toLocaleString('ko-KR')).map((part, index) => {
        if (index === 0) {
          return (
            <span key={`p${index}`}>
              {part}
              <span style={{
                fontWeight: '600',
                color: BBC_STYLE.TEXT_COLOR,
                backgroundColor: 'rgba(19, 128, 161, 0.08)',
                padding: '2px 6px',
                borderRadius: '3px'
              }}>
                {formatPriceForDisplay(avgPrice)}
              </span>
            </span>
          );
        }
        return <span key={`p${index}`}>{part}</span>;
      })}
      <span style={{
        margin: '0 10px',
        color: '#D0D0D0',
        fontWeight: '300'
      }}>｜</span>
      {translate('trendPage.marketTrends.narratives.price.single.avgDiscount', {
        rate: avgDiscount.toFixed(1)
      }).split(avgDiscount.toFixed(1)).map((part, index) => {
        if (index === 0) {
          return (
            <span key={`d${index}`}>
              {part}
              <span style={{
                fontWeight: '600',
                color: highDiscount ? BBC_STYLE.HIGHLIGHT_COLOR : BBC_STYLE.TEXT_COLOR,
                backgroundColor: highDiscount ? 'rgba(213, 94, 0, 0.08)' : 'rgba(0, 0, 0, 0.05)',
                padding: '2px 6px',
                borderRadius: '3px'
              }}>
                {avgDiscount.toFixed(1)}%
              </span>
            </span>
          );
        }
        return <span key={`d${index}`}>{part}</span>;
      })}
      {concentration > 40 && (
        <>
          <span style={{
            margin: '0 10px',
            color: '#D0D0D0',
            fontWeight: '300'
          }}>｜</span>
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <span style={{
              fontSize: '0.85rem',
              color: BBC_STYLE.HIGHLIGHT_COLOR
            }}>
              ⚠️
            </span>
            <span style={{
              fontWeight: '700',
              fontSize: '0.9rem',
              color: BBC_STYLE.HIGHLIGHT_COLOR
            }}>
              {translate('trendPage.marketTrends.narratives.price.single.rangeConcentration', {
                percentage: concentration
              })}
            </span>
          </span>
        </>
      )}
    </>
  );
};

// BBC-style metrics summary component with narrative insights
const MetricsSummary = ({ primary, secondary, hasSecondaryData, translate }) => { // eslint-disable-line no-unused-vars
  const primaryTotal = getTotalProducts(primary.price_analysis);
  const secondaryTotal = hasSecondaryData ? getTotalProducts(secondary.price_analysis) : 0;

  // Calculate insights for narrative
  const priceDiff = hasSecondaryData ?
    Math.abs(primary.price_analysis.avg_price - secondary.price_analysis.avg_price) : 0;

  // Determine which set has higher values
  const higherPrice = hasSecondaryData && secondary.price_analysis.avg_price > primary.price_analysis.avg_price;

  if (!hasSecondaryData) {
    // Single dataset - clean card style
    return (
      <div style={{
        borderTop: '2px solid ' + BBC_STYLE.PRIMARY_COLOR,
        paddingTop: '1.5rem',
        marginTop: '2rem',
      }}>
        {/* Metric cards in horizontal layout */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '1.5rem'
        }}>
          <div style={{
            textAlign: 'center',
            padding: '1rem',
            backgroundColor: '#FAFAFA',
            borderRadius: '8px',
            position: 'relative'
          }}>
            <div style={{
              position: 'absolute',
              top: '8px',
              left: '8px',
              width: '4px',
              height: '4px',
              borderRadius: '50%',
              backgroundColor: BBC_STYLE.PRIMARY_COLOR
            }} />
            <div style={{
              fontSize: '0.7rem',
              color: '#999',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: '8px'
            }}>
              {translate('trendPage.marketTrends.narratives.price.metrics.totalProducts')}
            </div>
            <div style={{
              fontSize: '2rem',
              fontWeight: '700',
              color: BBC_STYLE.PRIMARY_COLOR,
              lineHeight: '1'
            }}>
              {primaryTotal}
            </div>
            <div style={{
              fontSize: '0.75rem',
              color: '#666',
              marginTop: '4px'
            }}>
              {translate('trendPage.marketTrends.narratives.price.single.products')}
            </div>
          </div>

          <div style={{
            textAlign: 'center',
            padding: '1rem',
            backgroundColor: '#FAFAFA',
            borderRadius: '8px'
          }}>
            <div style={{
              fontSize: '0.7rem',
              color: '#999',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: '8px'
            }}>
              {translate('trendPage.marketTrends.narratives.price.metrics.avgPrice')}
            </div>
            <div style={{
              fontSize: '1.5rem',
              fontWeight: '600',
              color: BBC_STYLE.TEXT_COLOR,
              lineHeight: '1'
            }}>
              {(() => {
                const currentLang = localStorage.getItem('language') || 'ko';
                if (currentLang === 'en') {
                  return '₩' + primary.price_analysis.avg_price.toLocaleString('en-US');
                } else {
                  return primary.price_analysis.avg_price.toLocaleString('ko-KR') + '원';
                }
              })()}
            </div>
            <div style={{
              fontSize: '0.75rem',
              color: '#666',
              marginTop: '4px'
            }}>

            </div>
          </div>

          <div style={{
            textAlign: 'center',
            padding: '1rem',
            backgroundColor: primary.price_analysis.avg_discount > 10 ?
              'rgba(213, 94, 0, 0.05)' : '#FAFAFA',
            borderRadius: '8px'
          }}>
            <div style={{
              fontSize: '0.7rem',
              color: '#999',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: '8px'
            }}>
              {translate('trendPage.marketTrends.narratives.price.metrics.avgDiscount')}
            </div>
            <div style={{
              fontSize: '1.75rem',
              fontWeight: '600',
              color: primary.price_analysis.avg_discount > 10 ?
                BBC_STYLE.HIGHLIGHT_COLOR : BBC_STYLE.TEXT_COLOR,
              lineHeight: '1'
            }}>
              {primary.price_analysis.avg_discount.toFixed(1)}
            </div>
            <div style={{
              fontSize: '0.75rem',
              color: '#666',
              marginTop: '4px'
            }}>
              %
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Comparison mode - side-by-side with visual indicators
  return (
    <div style={{
      borderTop: '2px solid #E5E7EB',
      paddingTop: '1.5rem',
      marginTop: '2rem',
    }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '2rem',
        position: 'relative'
      }}>
        {/* Primary metrics */}
        <div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '1rem'
          }}>
            <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: BBC_STYLE.PRIMARY_COLOR
            }} />
            <span style={{
              fontSize: '0.85rem',
              color: '#666',
              fontWeight: '500',
              textTransform: 'uppercase',
              letterSpacing: '0.03em'
            }}>
              {translate('trendPage.filters.set1')}
            </span>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '1rem'
          }}>
            <div style={{
              padding: '0.75rem',
              borderLeft: '2px solid ' + BBC_STYLE.PRIMARY_COLOR,
              backgroundColor: '#FAFAFA',
              borderRadius: '0 4px 4px 0'
            }}>
              <div style={{
                fontSize: '0.65rem',
                color: '#999',
                textTransform: 'uppercase',
                marginBottom: '4px'
              }}>
                {translate('trendPage.marketTrends.narratives.price.metrics.productCount')}
              </div>
              <div style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: BBC_STYLE.PRIMARY_COLOR,
                lineHeight: '1'
              }}>
                {primaryTotal}
              </div>
            </div>

            <div style={{
              padding: '0.75rem',
              backgroundColor: '#FAFAFA',
              borderRadius: '4px',
              position: 'relative'
            }}>
              {priceDiff > 1000 && !higherPrice && (
                <div style={{
                  position: 'absolute',
                  top: '-8px',
                  right: '8px',
                  fontSize: '0.6rem',
                  color: BBC_STYLE.PRIMARY_COLOR,
                  fontWeight: '700'
                }}>
                  ↑
                </div>
              )}
              <div style={{
                fontSize: '0.65rem',
                color: '#999',
                textTransform: 'uppercase',
                marginBottom: '4px'
              }}>
                {translate('trendPage.marketTrends.narratives.price.metrics.average')}
              </div>
              <div style={{
                fontSize: '1.15rem',
                fontWeight: '600',
                color: BBC_STYLE.TEXT_COLOR,
                lineHeight: '1'
              }}>
                {(() => {
                  const currentLang = localStorage.getItem('language') || 'ko';
                  if (currentLang === 'en') {
                    return '₩' + primary.price_analysis.avg_price.toLocaleString('en-US');
                  } else {
                    return primary.price_analysis.avg_price.toLocaleString('ko-KR') + '원';
                  }
                })()}
              </div>
            </div>

            <div style={{
              padding: '0.75rem',
              backgroundColor: primary.price_analysis.avg_discount > 10 ?
                'rgba(213, 94, 0, 0.05)' : '#FAFAFA',
              borderRadius: '4px'
            }}>
              <div style={{
                fontSize: '0.65rem',
                color: '#999',
                textTransform: 'uppercase',
                marginBottom: '4px'
              }}>
                {translate('trendPage.marketTrends.narratives.price.metrics.discountRate')}
              </div>
              <div style={{
                fontSize: '1.25rem',
                fontWeight: '600',
                color: primary.price_analysis.avg_discount > 10 ?
                  BBC_STYLE.HIGHLIGHT_COLOR : BBC_STYLE.TEXT_COLOR,
                lineHeight: '1'
              }}>
                {primary.price_analysis.avg_discount.toFixed(1)}<span style={{ fontSize: '0.7rem' }}>%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Secondary metrics */}
        <div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '1rem'
          }}>
            <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: BBC_STYLE.SECONDARY_COLOR
            }} />
            <span style={{
              fontSize: '0.85rem',
              color: '#666',
              fontWeight: '500',
              textTransform: 'uppercase',
              letterSpacing: '0.03em'
            }}>
              {translate('trendPage.filters.set2')}
            </span>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '1rem'
          }}>
            <div style={{
              padding: '0.75rem',
              borderLeft: '2px solid ' + BBC_STYLE.SECONDARY_COLOR,
              backgroundColor: '#FAFAFA',
              borderRadius: '0 4px 4px 0'
            }}>
              <div style={{
                fontSize: '0.65rem',
                color: '#999',
                textTransform: 'uppercase',
                marginBottom: '4px'
              }}>
                {translate('trendPage.marketTrends.narratives.price.metrics.productCount')}
              </div>
              <div style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: BBC_STYLE.SECONDARY_COLOR,
                lineHeight: '1'
              }}>
                {secondaryTotal}
              </div>
            </div>

            <div style={{
              padding: '0.75rem',
              backgroundColor: '#FAFAFA',
              borderRadius: '4px',
              position: 'relative'
            }}>
              {priceDiff > 1000 && higherPrice && (
                <div style={{
                  position: 'absolute',
                  top: '-8px',
                  right: '8px',
                  fontSize: '0.6rem',
                  color: BBC_STYLE.SECONDARY_COLOR,
                  fontWeight: '700'
                }}>
                  ↑
                </div>
              )}
              <div style={{
                fontSize: '0.65rem',
                color: '#999',
                textTransform: 'uppercase',
                marginBottom: '4px'
              }}>
                {translate('trendPage.marketTrends.narratives.price.metrics.average')}
              </div>
              <div style={{
                fontSize: '1.15rem',
                fontWeight: '600',
                color: BBC_STYLE.TEXT_COLOR,
                lineHeight: '1'
              }}>
                {(() => {
                  const currentLang = localStorage.getItem('language') || 'ko';
                  if (currentLang === 'en') {
                    return '₩' + secondary.price_analysis.avg_price.toLocaleString('en-US');
                  } else {
                    return secondary.price_analysis.avg_price.toLocaleString('ko-KR') + '원';
                  }
                })()}
              </div>
            </div>

            <div style={{
              padding: '0.75rem',
              backgroundColor: secondary.price_analysis.avg_discount > 10 ?
                'rgba(213, 94, 0, 0.05)' : '#FAFAFA',
              borderRadius: '4px'
            }}>
              <div style={{
                fontSize: '0.65rem',
                color: '#999',
                textTransform: 'uppercase',
                marginBottom: '4px'
              }}>
                {translate('trendPage.marketTrends.narratives.price.metrics.discountRate')}
              </div>
              <div style={{
                fontSize: '1.25rem',
                fontWeight: '600',
                color: secondary.price_analysis.avg_discount > 10 ?
                  BBC_STYLE.HIGHLIGHT_COLOR : BBC_STYLE.TEXT_COLOR,
                lineHeight: '1'
              }}>
                {secondary.price_analysis.avg_discount.toFixed(1)}<span style={{ fontSize: '0.7rem' }}>%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Component
const PriceTrendChart = ({ priceData }) => {
  const { translate } = useLanguage();

  // Data validation
  if (!priceData?.primary?.price_analysis) {
    return <ErrorMessage message={translate('trendPage.marketTrends.errors.noPriceData')} />;
  }

  const { primary, secondary } = priceData;
  const hasSecondaryData = secondary?.price_analysis !== undefined;

  if (!primary.price_analysis.price_distribution?.labels ||
    !primary.price_analysis.price_distribution?.values) {
    return <ErrorMessage message={translate('trendPage.marketTrends.errors.invalidPriceData')} />;
  }

  const chartData = processChartData(primary, secondary, translate);
  const primaryKey = translate('trendPage.filters.set1');
  const secondaryKey = translate('trendPage.filters.set2');

  // Find max value for annotation
  const maxPrimaryValue = Math.max(...primary.price_analysis.price_distribution.values);
  const maxPrimaryIndex = primary.price_analysis.price_distribution.values.indexOf(maxPrimaryValue);
  const totalProducts = getTotalProducts(primary.price_analysis);

  let secondaryMaxIndex;
  if (hasSecondaryData) {
    const maxSecondaryValue = Math.max(...secondary.price_analysis.price_distribution.values);
    secondaryMaxIndex = secondary.price_analysis.price_distribution.values.indexOf(maxSecondaryValue);
  }

  return (
    <ChartContainer>
      {/* Narrative Title with visual effects */}
      <h2 style={{
        fontSize: '1.1rem',
        fontWeight: '600',
        color: BBC_STYLE.TEXT_COLOR,
        margin: '0 0 8px 0',
        lineHeight: '1.4'
      }}>
        {createNarrativeTitle(primary, secondary, hasSecondaryData, translate)}
      </h2>

      {/* Subtitle with insights and highlighting */}
      <div style={{
        fontSize: '0.85rem',
        color: BBC_STYLE.TEXT_COLOR,
        margin: '0 0 20px 0',
        lineHeight: '1.5'
      }}>
        {createSubtitle(primary, secondary, hasSecondaryData, translate)}
      </div>

      {/* BBC-Styled Chart */}
      <div className="chart-section">
        <ResponsiveContainer width="100%" height={350}>
          <BarChart
            data={chartData}
            margin={{ top: 65, right: 30, left: 20, bottom: 30 }}
          >
            {/* No need for arrow marker anymore - using simpler approach */}

            <XAxis
              dataKey="range"
              tick={{
                fontSize: BBC_STYLE.AXIS_SIZE,
                fill: BBC_STYLE.AXIS_COLOR
              }}
              axisLine={{ stroke: BBC_STYLE.AXIS_COLOR }}
              tickLine={false}
            />
            <YAxis
              tick={{
                fontSize: BBC_STYLE.AXIS_SIZE,
                fill: BBC_STYLE.AXIS_COLOR
              }}
              axisLine={false}
              tickLine={false}
              label={{
                value: translate('trendPage.marketTrends.chart.yAxisLabel'),
                angle: -90,
                position: 'insideLeft',
                style: {
                  fontSize: BBC_STYLE.AXIS_SIZE,
                  fill: BBC_STYLE.AXIS_COLOR
                }
              }}
            />
            <Tooltip
              cursor={{ fill: 'rgba(206, 206, 206, 0.15)' }}
              content={({ active, payload, label }) => {
                if (!active || !payload || !payload.length) return null;

                return (
                  <div style={{
                    background: 'white',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
                    minWidth: '140px'
                  }}>
                    <div style={{
                      fontSize: '11px',
                      fontWeight: '600',
                      color: '#999',
                      marginBottom: '10px'
                    }}>
                      {label}
                    </div>
                    {payload.map((entry, index) => (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '3px 0',
                          gap: '16px'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: entry.color
                          }} />
                          <span style={{ fontSize: '12px', color: '#666' }}>
                            {entry.name}
                          </span>
                        </div>
                        <span style={{
                          fontSize: '11px',
                          fontWeight: '500',
                          fontFamily: BBC_STYLE.FONT_FAMILY,
                          color: BBC_STYLE.TEXT_COLOR
                        }}>
                          {entry.value.toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                );
              }}
            />

            {/* Primary data bar with annotation */}
            <Bar
              dataKey={primaryKey}
              fill={BBC_STYLE.PRIMARY_COLOR}
              radius={[2, 2, 0, 0]}
            >
              {/* Highlight the max value bar */}
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={index === maxPrimaryIndex ? BBC_STYLE.PRIMARY_COLOR : BBC_STYLE.PRIMARY_COLOR}
                  fillOpacity={index === maxPrimaryIndex ? 1 : 0.8}
                />
              ))}
              {/* Add annotation to max value */}
              <LabelList
                dataKey={primaryKey}
                content={(props) => {
                  const { index } = props;
                  return <AnnotationLabel
                    {...props}
                    isMaxValue={true}
                    totalProducts={totalProducts}
                    isPrimary={true}
                    index={index}
                    dataIndex={maxPrimaryIndex}
                    translate={translate}
                  />
                }}
              />
            </Bar>

            {/* Secondary data bar if available */}
            {hasSecondaryData && (
              <Bar
                dataKey={secondaryKey}
                fill={BBC_STYLE.SECONDARY_COLOR}
                radius={[2, 2, 0, 0]}
                fillOpacity={0.7}
              >
                {/* Highlight the max value bar for secondary */}
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-secondary-${index}`}
                    fill={index === secondaryMaxIndex ? BBC_STYLE.SECONDARY_COLOR : BBC_STYLE.SECONDARY_COLOR}
                    fillOpacity={index === secondaryMaxIndex ? 0.9 : 0.6}
                  />
                ))}
                {/* Add annotation to max value for secondary */}
                <LabelList
                  dataKey={secondaryKey}
                  content={(props) => {
                    const { index } = props;
                    const secondaryTotal = getTotalProducts(secondary.price_analysis);
                    return <AnnotationLabel
                      {...props}
                      isMaxValue={true}
                      totalProducts={secondaryTotal}
                      isPrimary={false}
                      index={index}
                      dataIndex={secondaryMaxIndex}
                      translate={translate}
                    />
                  }}
                />
              </Bar>
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>

    </ChartContainer>
  );
};

PriceTrendChart.propTypes = {
  priceData: PropTypes.shape({
    primary: PropTypes.shape({
      price_analysis: PropTypes.shape({
        avg_price: PropTypes.number.isRequired,
        avg_discount: PropTypes.number.isRequired,
        price_distribution: PropTypes.shape({
          labels: PropTypes.arrayOf(PropTypes.string).isRequired,
          values: PropTypes.arrayOf(PropTypes.number).isRequired
        }).isRequired
      }).isRequired
    }).isRequired,
    secondary: PropTypes.shape({
      price_analysis: PropTypes.shape({
        avg_price: PropTypes.number.isRequired,
        avg_discount: PropTypes.number.isRequired,
        price_distribution: PropTypes.shape({
          labels: PropTypes.arrayOf(PropTypes.string).isRequired,
          values: PropTypes.arrayOf(PropTypes.number).isRequired
        }).isRequired
      })
    })
  }).isRequired
};

export default memo(PriceTrendChart);
